import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  AlertCircle,
  RefreshCw,
  Copy,
  Search,
  Sparkles,
  Loader2,
  X,
  ChevronRight,
  Terminal,
  Flag,
  CheckCircle,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const DAY_RANGES = [
  { value: 1, label: 'Last 24h' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
];
const LOG_LEVELS = ['ERROR', 'WARNING', 'ALL'];
const LEVEL_COLORS = {
  ERROR: 'bg-red-100 text-red-700 border-red-200',
  WARNING: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  CRITICAL: 'bg-red-200 text-red-900 border-red-300',
};

const AIBackendDiagnostics = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.is_superadmin || user?.role === 'superadmin';

  // Tab
  const [activeTab, setActiveTab] = useState('chat');

  // Chat failures state
  const [days, setDays] = useState(7);
  const [userFilter, setUserFilter] = useState('');
  const [sessionFilter, setSessionFilter] = useState('');
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Flagged messages state
  const [flagDays, setFlagDays] = useState(30);
  const [flagCategory, setFlagCategory] = useState('');
  const [flagShowReviewed, setFlagShowReviewed] = useState(false);
  const [flagRecords, setFlagRecords] = useState([]);
  const [flagTotal, setFlagTotal] = useState(0);
  const [flagLoading, setFlagLoading] = useState(false);
  const [markingReviewed, setMarkingReviewed] = useState(null);

  // Server logs state
  const [logDays, setLogDays] = useState(1);
  const [logLevel, setLogLevel] = useState('ERROR');
  const [logEndpointFilter, setLogEndpointFilter] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [logRecords, setLogRecords] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logLoading, setLogLoading] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [logDetail, setLogDetail] = useState(null);
  const [logDetailLoading, setLogDetailLoading] = useState(false);
  const [logDiagnosing, setLogDiagnosing] = useState(false);

  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const fetchList = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (userFilter.trim()) params.append('user_id', userFilter.trim());
      if (sessionFilter.trim()) params.append('session_id', sessionFilter.trim());
      const { data } = await axios.get(
        `${API_BASE}/admin/chat-diagnostics?${params.toString()}`,
        { headers: authHeader() }
      );
      setRecords(data.records || []);
      setTotal(data.total || 0);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Failed to load diagnostics: ${msg}`);
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [days, userFilter, sessionFilter, isSuperAdmin]);

  const fetchDetail = useCallback(async (messageId) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const { data } = await axios.get(
        `${API_BASE}/admin/chat-diagnostics/${messageId}`,
        { headers: authHeader() }
      );
      setDetail(data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Failed to load detail: ${msg}`);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const regenerateDiagnosis = async () => {
    if (!selectedId) return;
    setRegenerating(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/admin/chat-diagnostics/${selectedId}/regenerate-diagnosis`,
        {},
        { headers: authHeader() }
      );
      setDetail((prev) => (prev ? { ...prev, ai_diagnosis: data.ai_diagnosis } : prev));
      toast.success('Diagnosis regenerated');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Regenerate failed: ${msg}`);
    } finally {
      setRegenerating(false);
    }
  };

  const fetchLogList = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLogLoading(true);
    try {
      const params = new URLSearchParams({ days: String(logDays), level: logLevel });
      if (logEndpointFilter.trim()) params.append('endpoint', logEndpointFilter.trim());
      if (logSearch.trim()) params.append('search', logSearch.trim());
      const { data } = await axios.get(
        `${API_BASE}/admin/server-logs?${params.toString()}`,
        { headers: authHeader() }
      );
      setLogRecords(data.records || []);
      setLogTotal(data.total || 0);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Failed to load server logs: ${msg}`);
      setLogRecords([]);
      setLogTotal(0);
    } finally {
      setLogLoading(false);
    }
  }, [logDays, logLevel, logEndpointFilter, logSearch, isSuperAdmin]);

  const fetchLogDetail = useCallback(async (logId) => {
    setLogDetailLoading(true);
    setLogDetail(null);
    try {
      const { data } = await axios.get(
        `${API_BASE}/admin/server-logs/${logId}`,
        { headers: authHeader() }
      );
      setLogDetail(data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Failed to load log detail: ${msg}`);
    } finally {
      setLogDetailLoading(false);
    }
  }, []);

  const runLogDiagnosis = async () => {
    if (!selectedLogId) return;
    setLogDiagnosing(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/admin/server-logs/${selectedLogId}/diagnose`,
        {},
        { headers: authHeader() }
      );
      setLogDetail((prev) => prev ? { ...prev, diagnosis: data.diagnosis } : prev);
      toast.success('Diagnosis complete');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Diagnosis failed: ${msg}`);
    } finally {
      setLogDiagnosing(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
  }, [selectedId, fetchDetail]);

  const fetchFlaggedMessages = useCallback(async () => {
    setFlagLoading(true);
    try {
      const params = new URLSearchParams({ days: String(flagDays) });
      if (flagCategory) params.set('category', flagCategory);
      if (!flagShowReviewed) params.set('reviewed', 'false');
      const res = await axios.get(`${API_BASE}/admin/flagged-messages?${params}`, { headers: authHeader() });
      setFlagRecords(res.data.records || []);
      setFlagTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load flagged messages');
    } finally {
      setFlagLoading(false);
    }
  }, [flagDays, flagCategory, flagShowReviewed]);

  const markReviewed = async (msgId) => {
    setMarkingReviewed(msgId);
    try {
      await axios.patch(`${API_BASE}/admin/flagged-messages/${msgId}/review`, {}, { headers: authHeader() });
      setFlagRecords(prev => prev.filter(r => flagShowReviewed || r.id !== msgId));
      toast.success('Marked as reviewed');
    } catch (e) {
      toast.error('Failed to mark reviewed');
    } finally {
      setMarkingReviewed(null);
    }
  };

  useEffect(() => {
    if (activeTab === 'logs') fetchLogList();
    if (activeTab === 'flagged') fetchFlaggedMessages();
  }, [activeTab, fetchLogList, fetchFlaggedMessages]);

  useEffect(() => {
    if (selectedLogId) fetchLogDetail(selectedLogId);
  }, [selectedLogId, fetchLogDetail]);

  const copyAsBugReport = async () => {
    if (!detail) return;
    const m = detail.message || {};
    const ec = detail.error_context || {};
    const diag = detail.ai_diagnosis || {};
    const precedingUser =
      (detail.session_history || [])
        .filter((h) => h.role === 'user' && new Date(h.created_at) < new Date(m.created_at))
        .slice(-1)[0]?.content || '';
    const block = [
      `## AI Backend Failure — session ${m.session_id}`,
      `- **User:** ${m.user_id}`,
      `- **Time:** ${m.created_at}`,
      `- **User message:** ${JSON.stringify(precedingUser)}`,
      `- **Routed model:** ${ec.routed_model || '—'}`,
      `- **Error:** ${ec.error_class || '—'}: ${(ec.error_message || '').slice(0, 300)}`,
      `- **Phase:** ${ec.phase || '—'}`,
      ``,
      `**AI diagnosis:** ${diag.diagnosis || '(not available)'}`,
      `**Next check:** ${diag.suggested_check || '(not available)'}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(block);
      toast.success('Bug report copied to clipboard');
    } catch {
      toast.error('Clipboard unavailable — select and copy manually');
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen p-8 max-w-3xl mx-auto">
        <Card className="glass border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <div>
                <div className="font-semibold text-slate-800">Superadmin access required</div>
                <div className="text-sm text-slate-600">
                  This page is restricted to the superadmin role.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-display font-semibold text-slate-800 flex items-center gap-2">
          <AlertCircle className="w-7 h-7 text-orange-500" />
          AI Backend Diagnostics
        </h1>
        <p className="text-slate-600 mt-1">
          Failed chat responses and server-side errors with AI-generated root-cause analysis.
        </p>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 border rounded-lg p-1 bg-white/60 mb-6 w-fit">
        <button
          onClick={() => setActiveTab('chat')}
          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${
            activeTab === 'chat' ? 'bg-emerald-500 text-white' : 'text-slate-700 hover:bg-slate-100'
          }`}
        >
          <AlertCircle className="w-4 h-4" />
          Chat Failures
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${
            activeTab === 'logs' ? 'bg-emerald-500 text-white' : 'text-slate-700 hover:bg-slate-100'
          }`}
        >
          <Terminal className="w-4 h-4" />
          Server Logs
        </button>
        <button
          onClick={() => setActiveTab('flagged')}
          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${
            activeTab === 'flagged' ? 'bg-amber-500 text-white' : 'text-slate-700 hover:bg-slate-100'
          }`}
        >
          <Flag className="w-4 h-4" />
          Flagged
        </button>
      </div>

      {activeTab === 'chat' && <><Card className="glass border-0 mb-6">
        <CardHeader>
          <CardTitle className="text-base font-medium">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex gap-1 border rounded-lg p-1 bg-white/60">
              {DAY_RANGES.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDays(opt.value)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    days === opt.value
                      ? 'bg-emerald-500 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex-1 min-w-[180px]">
              <Label htmlFor="userFilter" className="text-xs text-slate-600">
                User ID (e.g. laravel_10)
              </Label>
              <Input
                id="userFilter"
                placeholder="laravel_10"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
              />
            </div>
            <div className="flex-1 min-w-[180px]">
              <Label htmlFor="sessionFilter" className="text-xs text-slate-600">
                Session ID
              </Label>
              <Input
                id="sessionFilter"
                placeholder="session_..."
                value={sessionFilter}
                onChange={(e) => setSessionFilter(e.target.value)}
              />
            </div>
            <Button variant="outline" onClick={fetchList} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              <span className="ml-2">Refresh</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="glass border-0 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center justify-between">
              <span>Failures ({total})</span>
              {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            </CardTitle>
            <CardDescription>
              Click a row to see the AI-generated diagnosis and full context.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {records.length === 0 && !loading ? (
              <div className="text-center py-12 text-slate-500">
                No failed messages in the selected window. 🎉
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-600 border-b">
                      <th className="py-2 pr-2">Time</th>
                      <th className="py-2 pr-2">User</th>
                      <th className="py-2 pr-2">User message</th>
                      <th className="py-2 pr-2">Model</th>
                      <th className="py-2 pr-2">Error</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r) => (
                      <tr
                        key={r.message_id}
                        onClick={() => setSelectedId(r.message_id)}
                        className={`border-b cursor-pointer hover:bg-slate-50 ${
                          selectedId === r.message_id ? 'bg-emerald-50' : ''
                        }`}
                      >
                        <td className="py-2 pr-2 whitespace-nowrap text-xs text-slate-600">
                          {formatDate(r.created_at)}
                        </td>
                        <td className="py-2 pr-2 font-mono text-xs">{r.user_id}</td>
                        <td className="py-2 pr-2 max-w-[260px] truncate" title={r.preceding_user_message}>
                          {r.preceding_user_message || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="py-2 pr-2 text-xs">
                          {r.routed_model ? (
                            <Badge variant="outline" className="font-mono text-xs">
                              {r.routed_model.replace('anthropic/', '')}
                            </Badge>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="py-2 pr-2 text-xs text-red-600">
                          {r.error_class || r.status || 'empty'}
                        </td>
                        <td className="py-2 text-slate-400">
                          <ChevronRight className="w-4 h-4" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass border-0 lg:col-span-1 h-fit sticky top-4">
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                Detail
              </span>
              {detail && (
                <button
                  onClick={() => {
                    setSelectedId(null);
                    setDetail(null);
                  }}
                  className="text-slate-400 hover:text-slate-700"
                  aria-label="Close detail"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedId && (
              <div className="text-sm text-slate-500 py-8 text-center">
                Select a row to see context and diagnosis.
              </div>
            )}
            {selectedId && detailLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
              </div>
            )}
            {detail && !detailLoading && (
              <div className="space-y-4">
                <div>
                  <div className="text-xs uppercase text-slate-500 mb-1">AI diagnosis</div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                    <div className="text-slate-800 mb-2">
                      {detail.ai_diagnosis?.diagnosis || (
                        <span className="text-slate-500 italic">Not generated.</span>
                      )}
                    </div>
                    {detail.ai_diagnosis?.suggested_check && (
                      <div className="text-xs text-emerald-800">
                        <span className="font-semibold">Next check:</span>{' '}
                        {detail.ai_diagnosis.suggested_check}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={regenerateDiagnosis}
                      disabled={regenerating}
                    >
                      {regenerating ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3 h-3" />
                      )}
                      <span className="ml-1 text-xs">Regenerate</span>
                    </Button>
                    <Button size="sm" variant="outline" onClick={copyAsBugReport}>
                      <Copy className="w-3 h-3" />
                      <span className="ml-1 text-xs">Copy as bug report</span>
                    </Button>
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase text-slate-500 mb-1">Error context</div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs font-mono whitespace-pre-wrap">
                    {detail.error_context
                      ? JSON.stringify(detail.error_context, null, 2)
                      : '(none recorded — pre-instrumentation row)'}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase text-slate-500 mb-1">
                    Session history ({detail.session_history?.length || 0} messages)
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-2 bg-white/60 border border-slate-200 rounded-lg p-2">
                    {(detail.session_history || []).map((h) => (
                      <div
                        key={h.message_id}
                        className={`text-xs p-2 rounded ${
                          h.role === 'user'
                            ? 'bg-blue-50 text-slate-800'
                            : h.content
                            ? 'bg-slate-50 text-slate-700'
                            : 'bg-red-50 text-red-700 italic'
                        }`}
                      >
                        <div className="font-semibold">{h.role}</div>
                        <div className="whitespace-pre-wrap">
                          {h.content || '(empty)'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {detail.llm_usage_record && (
                  <div>
                    <div className="text-xs uppercase text-slate-500 mb-1">LLM usage record</div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 text-xs font-mono">
                      <div>status: {detail.llm_usage_record.status}</div>
                      <div>model: {detail.llm_usage_record.model}</div>
                      <div>
                        tokens: in={detail.llm_usage_record.input_tokens} out=
                        {detail.llm_usage_record.output_tokens}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </>}

      {activeTab === 'logs' && <>
        {/* Server Logs filters */}
        <Card className="glass border-0 mb-6">
          <CardHeader>
            <CardTitle className="text-base font-medium">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex gap-1 border rounded-lg p-1 bg-white/60">
                {DAY_RANGES.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setLogDays(opt.value)}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      logDays === opt.value ? 'bg-emerald-500 text-white' : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1 border rounded-lg p-1 bg-white/60">
                {LOG_LEVELS.map((lvl) => (
                  <button
                    key={lvl}
                    onClick={() => setLogLevel(lvl)}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      logLevel === lvl ? 'bg-emerald-500 text-white' : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
              <div className="flex-1 min-w-[160px]">
                <Label className="text-xs text-slate-600">Endpoint</Label>
                <Input
                  placeholder="/api/chat..."
                  value={logEndpointFilter}
                  onChange={(e) => setLogEndpointFilter(e.target.value)}
                />
              </div>
              <div className="flex-1 min-w-[160px]">
                <Label className="text-xs text-slate-600">Search message</Label>
                <Input
                  placeholder="error text..."
                  value={logSearch}
                  onChange={(e) => setLogSearch(e.target.value)}
                />
              </div>
              <Button variant="outline" onClick={fetchLogList} disabled={logLoading}>
                {logLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                <span className="ml-2">Refresh</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Log list */}
          <Card className="glass border-0 lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base font-medium flex items-center justify-between">
                <span>Server Logs ({logTotal})</span>
                {logLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              </CardTitle>
              <CardDescription>Click a row to see the full message, traceback, and AI diagnosis.</CardDescription>
            </CardHeader>
            <CardContent>
              {logRecords.length === 0 && !logLoading ? (
                <div className="text-center py-12 text-slate-500">
                  No log entries in the selected window. 🎉
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-600 border-b">
                        <th className="py-2 pr-2">Time</th>
                        <th className="py-2 pr-2">Level</th>
                        <th className="py-2 pr-2">Logger</th>
                        <th className="py-2 pr-2">Endpoint</th>
                        <th className="py-2 pr-2">Message</th>
                        <th className="py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {logRecords.map((r) => (
                        <tr
                          key={r.log_id}
                          onClick={() => setSelectedLogId(r.log_id)}
                          className={`border-b cursor-pointer hover:bg-slate-50 ${
                            selectedLogId === r.log_id ? 'bg-emerald-50' : ''
                          }`}
                        >
                          <td className="py-2 pr-2 whitespace-nowrap text-xs text-slate-600">
                            {formatDate(r.timestamp)}
                          </td>
                          <td className="py-2 pr-2">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded border ${LEVEL_COLORS[r.level] || 'bg-slate-100 text-slate-700'}`}>
                              {r.level}
                            </span>
                          </td>
                          <td className="py-2 pr-2 font-mono text-xs max-w-[120px] truncate" title={r.logger_name}>
                            {(r.logger_name || '').replace('app.', '')}
                          </td>
                          <td className="py-2 pr-2 font-mono text-xs max-w-[120px] truncate" title={r.endpoint}>
                            {r.endpoint || '—'}
                          </td>
                          <td className="py-2 pr-2 text-xs max-w-[200px] truncate text-slate-700" title={r.message}>
                            {r.exc_type ? (
                              <span className="text-red-600">{r.exc_type}: </span>
                            ) : null}
                            {(r.message || '').slice(0, 120)}
                          </td>
                          <td className="py-2 text-slate-400">
                            <ChevronRight className="w-4 h-4" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Log detail */}
          <Card className="glass border-0 lg:col-span-1 h-fit sticky top-4">
            <CardHeader>
              <CardTitle className="text-base font-medium flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-600" />
                  Detail
                </span>
                {logDetail && (
                  <button
                    onClick={() => { setSelectedLogId(null); setLogDetail(null); }}
                    className="text-slate-400 hover:text-slate-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedLogId && (
                <div className="text-sm text-slate-500 py-8 text-center">Select a row to inspect.</div>
              )}
              {selectedLogId && logDetailLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
                </div>
              )}
              {logDetail && !logDetailLoading && (
                <div className="space-y-4">
                  {/* Request context */}
                  <div className="flex flex-wrap gap-2 text-xs font-mono">
                    {logDetail.method && (
                      <span className="bg-slate-100 px-2 py-0.5 rounded">{logDetail.method}</span>
                    )}
                    {logDetail.endpoint && (
                      <span className="bg-slate-100 px-2 py-0.5 rounded truncate max-w-[180px]">{logDetail.endpoint}</span>
                    )}
                    {logDetail.request_id && (
                      <span className="bg-slate-100 px-2 py-0.5 rounded text-slate-500">req:{logDetail.request_id}</span>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <div className="text-xs uppercase text-slate-500 mb-1">Message</div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs font-mono whitespace-pre-wrap max-h-40 overflow-y-auto">
                      {logDetail.message || '—'}
                    </div>
                  </div>

                  {/* Traceback */}
                  {logDetail.traceback && (
                    <div>
                      <div className="text-xs uppercase text-slate-500 mb-1">Traceback</div>
                      <div className="bg-red-50 border border-red-100 rounded-lg p-2 text-xs font-mono whitespace-pre-wrap max-h-48 overflow-y-auto text-red-800">
                        {logDetail.traceback}
                      </div>
                    </div>
                  )}

                  {/* AI diagnosis */}
                  <div>
                    <div className="text-xs uppercase text-slate-500 mb-1">AI diagnosis</div>
                    {logDetail.diagnosis ? (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                        <div className="text-slate-800 mb-2">{logDetail.diagnosis.diagnosis}</div>
                        {logDetail.diagnosis.suggested_check && (
                          <div className="text-xs text-emerald-800">
                            <span className="font-semibold">Next check:</span> {logDetail.diagnosis.suggested_check}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-slate-500 italic">Not yet generated.</div>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      onClick={runLogDiagnosis}
                      disabled={logDiagnosing}
                    >
                      {logDiagnosing ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Sparkles className="w-3 h-3" />
                      )}
                      <span className="ml-1 text-xs">
                        {logDetail.diagnosis ? 'Re-diagnose' : 'Diagnose'}
                      </span>
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </>}

      {activeTab === 'flagged' && <>
        <Card className="glass border-0 mb-4">
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Period</Label>
                <div className="flex gap-1">
                  {[7, 30, 90].map(d => (
                    <button key={d} onClick={() => setFlagDays(d)}
                      className={`px-3 py-1 text-xs rounded-md border transition-colors ${flagDays === d ? 'bg-amber-500 text-white border-amber-500' : 'text-slate-700 border-slate-200 hover:bg-slate-100'}`}>
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Category</Label>
                <select value={flagCategory} onChange={e => setFlagCategory(e.target.value)}
                  className="border rounded-md text-sm px-2 py-1.5 bg-white">
                  <option value="">All categories</option>
                  <option value="medical_diagnosis">Medical diagnosis</option>
                  <option value="urgent_safety">Urgent safety</option>
                  <option value="out_of_scope">Out of scope</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer pb-1">
                <input type="checkbox" checked={flagShowReviewed} onChange={e => setFlagShowReviewed(e.target.checked)} className="rounded" />
                Show reviewed
              </label>
              <Button variant="outline" size="sm" onClick={fetchFlaggedMessages} disabled={flagLoading}>
                <RefreshCw className={`w-3 h-3 mr-1 ${flagLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Flag className="w-4 h-4 text-amber-500" />
              Flagged Messages ({flagTotal})
            </CardTitle>
            <CardDescription className="text-xs">Messages where Mira was directed to decline or escalate</CardDescription>
          </CardHeader>
          <CardContent>
            {flagLoading ? (
              <div className="flex items-center gap-2 text-slate-500 py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading...
              </div>
            ) : flagRecords.length === 0 ? (
              <p className="text-slate-500 text-sm py-4">No flagged messages in this period.</p>
            ) : (
              <div className="space-y-2">
                {flagRecords.map(r => {
                  const CATEGORY_COLORS = {
                    medical_diagnosis: 'bg-purple-100 text-purple-700 border-purple-200',
                    urgent_safety: 'bg-red-100 text-red-700 border-red-200',
                    out_of_scope: 'bg-slate-100 text-slate-600 border-slate-200',
                  };
                  const CATEGORY_LABELS = {
                    medical_diagnosis: 'Medical',
                    urgent_safety: 'Urgent',
                    out_of_scope: 'Out of scope',
                  };
                  return (
                    <div key={r.id} className="p-3 rounded-lg border bg-white/60 space-y-2">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded border font-medium ${CATEGORY_COLORS[r.category] || 'bg-slate-100 text-slate-600'}`}>
                              {CATEGORY_LABELS[r.category] || r.category}
                            </span>
                            <span className="text-xs text-slate-400">{new Date(r.flagged_at).toLocaleString()}</span>
                            {r.reviewed && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Reviewed</span>}
                          </div>
                          <p className="text-sm text-slate-700">{r.message}</p>
                          {r.user_id && <p className="text-xs text-slate-400 mt-0.5">User: {r.user_id}</p>}
                        </div>
                        {!r.reviewed && (
                          <Button variant="outline" size="sm" onClick={() => markReviewed(r.id)} disabled={markingReviewed === r.id}
                            className="shrink-0 text-xs">
                            {markingReviewed === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                            Review
                          </Button>
                        )}
                      </div>
                      {r.ai_response && (
                        <div className="mt-1 pl-3 border-l-2 border-slate-200">
                          <p className="text-xs text-slate-400 mb-0.5 font-medium">Mira's response</p>
                          <p className="text-xs text-slate-600 leading-relaxed">{r.ai_response}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </>}
    </div>
  );
};

export default AIBackendDiagnostics;
